import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import './mobile-map-zoom.css';

interface Point { x: number; y: number }
interface ZoomView extends Point { scale: number }
type Gesture =
  | { type: 'pan'; start: Point; origin: Point; moved: boolean }
  | { type: 'pinch'; distance: number; midpoint: Point; origin: ZoomView; moved: boolean }
  | null;

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const MOVE_THRESHOLD = 4;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function distance(a: Point, b: Point) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function boundedView(view: ZoomView, width: number, height: number): ZoomView {
  const maxX = Math.max(0, width * (view.scale - 1) / 2);
  const maxY = Math.max(0, height * (view.scale - 1) / 2);
  return { scale: clamp(view.scale, MIN_ZOOM, MAX_ZOOM), x: clamp(view.x, -maxX, maxX), y: clamp(view.y, -maxY, maxY) };
}

export function MobileMapZoom({ children, label }: { children: ReactNode; label: string }) {
  const [view, setView] = useState<ZoomView>({ scale: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  const viewportRef = useRef<HTMLDivElement>(null);
  const pointersRef = useRef(new Map<number, Point>());
  const gestureRef = useRef<Gesture>(null);
  const suppressClickRef = useRef(false);
  const suppressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 760px)');
    const resetForDesktop = () => {
      if (mobileQuery.matches) return;
      pointersRef.current.clear();
      gestureRef.current = null;
      const initialView = { scale: MIN_ZOOM, x: 0, y: 0 };
      viewRef.current = initialView;
      setView(initialView);
    };
    resetForDesktop();
    mobileQuery.addEventListener('change', resetForDesktop);
    return () => {
      mobileQuery.removeEventListener('change', resetForDesktop);
      if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
    };
  }, []);

  const updateView = (next: ZoomView) => {
    const bounds = viewportRef.current?.getBoundingClientRect();
    const bounded = boundedView(next, bounds?.width ?? 0, bounds?.height ?? 0);
    viewRef.current = bounded;
    setView(bounded);
  };

  const zoomBy = (factor: number) => {
    const current = viewRef.current;
    const scale = clamp(current.scale * factor, MIN_ZOOM, MAX_ZOOM);
    if (scale === MIN_ZOOM) updateView({ scale: MIN_ZOOM, x: 0, y: 0 });
    else updateView({ ...current, scale });
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!window.matchMedia('(max-width: 760px)').matches || event.pointerType !== 'touch') return;
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* The browser may already own this pointer. */ }
    const pointers = pointersRef.current;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.size >= 2) {
      const pair = [...pointers.values()].slice(-2);
      const [first, second] = pair;
      if (first && second) gestureRef.current = { type: 'pinch', distance: Math.max(1, distance(first, second)), midpoint: midpoint(first, second), origin: viewRef.current, moved: false };
    } else if (viewRef.current.scale > MIN_ZOOM) {
      gestureRef.current = { type: 'pan', start: { x: event.clientX, y: event.clientY }, origin: { x: viewRef.current.x, y: viewRef.current.y }, moved: false };
    } else {
      gestureRef.current = null;
    }
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const pointers = pointersRef.current;
    if (!pointers.has(event.pointerId)) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const gesture = gestureRef.current;
    const bounds = viewportRef.current?.getBoundingClientRect();
    if (!gesture || !bounds) return;
    event.preventDefault();
    if (gesture.type === 'pinch' && pointers.size >= 2) {
      const pair = [...pointers.values()].slice(-2);
      const [first, second] = pair;
      if (!first || !second) return;
      const currentMidpoint = midpoint(first, second);
      const currentDistance = distance(first, second);
      if (Math.abs(currentDistance - gesture.distance) > MOVE_THRESHOLD || distance(currentMidpoint, gesture.midpoint) > MOVE_THRESHOLD) gesture.moved = true;
      const scale = clamp(gesture.origin.scale * currentDistance / gesture.distance, MIN_ZOOM, MAX_ZOOM);
      updateView({ scale, x: gesture.origin.x + currentMidpoint.x - gesture.midpoint.x, y: gesture.origin.y + currentMidpoint.y - gesture.midpoint.y });
      return;
    }
    if (gesture.type === 'pan') {
      const dx = event.clientX - gesture.start.x;
      const dy = event.clientY - gesture.start.y;
      if (Math.hypot(dx, dy) > MOVE_THRESHOLD) gesture.moved = true;
      updateView({ scale: viewRef.current.scale, x: gesture.origin.x + dx, y: gesture.origin.y + dy });
    }
  };

  const onPointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const pointers = pointersRef.current;
    const gesture = gestureRef.current;
    if (gesture?.moved) {
      suppressClickRef.current = true;
      if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
      suppressTimerRef.current = setTimeout(() => { suppressClickRef.current = false; }, 350);
    }
    pointers.delete(event.pointerId);
    if (pointers.size === 1 && viewRef.current.scale > MIN_ZOOM) {
      const [pointerId, point] = [...pointers.entries()][0] ?? [];
      if (pointerId !== undefined && point) gestureRef.current = { type: 'pan', start: point, origin: { x: viewRef.current.x, y: viewRef.current.y }, moved: false };
    } else if (pointers.size === 0) {
      gestureRef.current = null;
    }
  };

  const onClickCapture = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
    if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
  };

  const zoomed = view.scale > MIN_ZOOM;
  return <div
    className={`mobile-map-zoom${zoomed ? ' is-zoomed' : ''}${gestureRef.current ? ' is-gesturing' : ''}`}
    ref={viewportRef}
    onPointerDown={onPointerDown}
    onPointerMove={onPointerMove}
    onPointerUp={onPointerEnd}
    onPointerCancel={onPointerEnd}
    onClickCapture={onClickCapture}
  >
    <div className="mobile-map-zoom-content" style={{ transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})` }}>{children}</div>
    <div className="mobile-map-zoom-controls" role="group" aria-label={`Controles de zoom ${label}`}>
      <button type="button" aria-label={`Aumentar zoom ${label}`} onPointerDown={(event) => event.stopPropagation()} onClick={() => zoomBy(1.4)} disabled={view.scale >= MAX_ZOOM}>+</button>
      <button type="button" aria-label={`Diminuir zoom ${label}`} onPointerDown={(event) => event.stopPropagation()} onClick={() => zoomBy(1 / 1.4)} disabled={view.scale <= MIN_ZOOM}>−</button>
      <button type="button" aria-label={`Redefinir zoom ${label}`} onPointerDown={(event) => event.stopPropagation()} onClick={() => updateView({ scale: MIN_ZOOM, x: 0, y: 0 })}>↺</button>
    </div>
  </div>;
}
